import {
    useLayoutEffect,
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    AccessibilityInfo,
    type TextStyle,
    Platform
} from "react-native";
import type {
    DropdownMenuHeadingInsetConstantType,
    DropdownMenuRootStateConstantType,
    DropdownMenuRootStateType,
    DropdownMenuOpenModeType
} from "./type";
import {
    useDropdownMenuLevelContext
} from "./context";
import {
    getIsDropdownMenuKeyboardFocus,
    getDropdownMenuActiveElement,
    focusDropdownMenuElement,
    getDropdownMenuElement
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    getOptionRowInset
} from "../optionRow/stylesheet";

const DROPDOWN_MENU_SURFACE_SELECTOR = "[data-ncore-surface=\"popover\"]";

export const useDropdownMenuRootState = ({
    isOpen: isOpenProp,
    isDefaultOpen,
    onOpenChange,
    anchorRef
}: DropdownMenuRootStateConstantType): DropdownMenuRootStateType => {
    const previousFocusRef = useRef<HTMLElement | null>(null);

    const isOpenRef = useRef(false);

    const wasOpenRef = useRef(false);

    const isFocusRecordedRef = useRef(false);

    const [
        isOpenState,
        setIsOpenState
    ] = useState(isDefaultOpen);

    const [
        openMode,
        setOpenMode
    ] = useState<DropdownMenuOpenModeType>("pointer");

    const isOpen = isOpenProp ?? isOpenState;

    isOpenRef.current = isOpen;

    const handleOpenChange = useCallback((nextIsOpen: boolean, nextOpenMode?: DropdownMenuOpenModeType) => {
        if(nextIsOpen === isOpenRef.current) {
            return;
        }

        if(nextIsOpen) {
            const activeElement = getDropdownMenuActiveElement();

            previousFocusRef.current = activeElement;

            isFocusRecordedRef.current = true;

            setOpenMode(nextOpenMode ?? (getIsDropdownMenuKeyboardFocus(activeElement) ? "first" : "pointer"));
        }

        setIsOpenState(nextIsOpen);

        onOpenChange?.(nextIsOpen);
    }, [
        onOpenChange
    ]);

    const handleClose = useCallback(() => {
        handleOpenChange(false);
    }, [
        handleOpenChange
    ]);

    const handleTabKey = useCallback(() => {
        focusDropdownMenuElement(previousFocusRef.current);

        handleOpenChange(false);
    }, [
        handleOpenChange
    ]);

    useLayoutEffect(() => {
        if(!isOpen) {
            isFocusRecordedRef.current = false;

            return;
        }

        if(isFocusRecordedRef.current) {
            return;
        }

        isFocusRecordedRef.current = true;

        previousFocusRef.current = getDropdownMenuActiveElement();
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(isOpen) {
            wasOpenRef.current = true;

            return;
        }

        if(!wasOpenRef.current) {
            return;
        }

        wasOpenRef.current = false;

        if(Platform.OS !== "web") {
            if(anchorRef.current) {
                AccessibilityInfo.sendAccessibilityEvent(anchorRef.current, "focus");
            }

            return;
        }

        const activeElement = getDropdownMenuActiveElement();

        const isFocusInsideMenu = !!getDropdownMenuElement(activeElement)?.closest(DROPDOWN_MENU_SURFACE_SELECTOR);

        const isFocusLost = !activeElement || activeElement === document.body;

        if(isFocusInsideMenu || isFocusLost) {
            focusDropdownMenuElement(previousFocusRef.current);
        }
    }, [
        isOpen
    ]);

    return {
        handleOpenChange,
        handleTabKey,
        handleClose,
        openMode,
        isOpen
    };
};

export const useDropdownMenuHeadingInset = ({
    customTheme,
    part
}: DropdownMenuHeadingInsetConstantType): TextStyle | undefined => {
    const levelContext = useDropdownMenuLevelContext({
        part
    });

    const {
        controlHeights,
        touchTargets,
        typography,
        iconSizes,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    if(!levelContext?.isReserveLeading) {
        return undefined;
    }

    return {
        marginStart: getOptionRowInset({
            variant: "popover",
            controlHeights,
            touchTargets,
            pointerType,
            typography,
            iconSizes,
            spaces
        })
    };
};
